import { useState } from 'react';
import { Alert, Button, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import type { Subject } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface WatchlistPageProps {
  readonly tenantId: string;
  readonly subjects: Subject[];
  readonly onCreateSubject: (payload: { tenantId: string; displayName: string; notes?: string }) => Promise<Subject>;
  readonly onAddFace: (subjectId: string, payload: { tenantId: string; sourceImageName: string; imageBase64: string }) => Promise<void>;
}

export default function WatchlistPage({ tenantId, subjects, onCreateSubject, onAddFace }: WatchlistPageProps) {
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [uploadingSubjectId, setUploadingSubjectId] = useState('');

  const create = async () => {
    try {
      setError('');
      await onCreateSubject({ tenantId, displayName, notes: notes || undefined });
      setDisplayName('');
      setNotes('');
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Unable to create subject.');
    }
  };

  const uploadFace = async (subjectId: string, files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingSubjectId(subjectId);
      setError('');
      const imageBase64 = await fileToBase64(file);
      await onAddFace(subjectId, {
        tenantId,
        sourceImageName: file.name,
        imageBase64,
      });
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Unable to upload subject face.');
    } finally {
      setUploadingSubjectId('');
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={7}>
        <PageSection title="Watchlist Subjects" subtitle="Known persons indexed into the tenant collection strategy.">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subject</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Face</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subjects.map((subject) => (
                <TableRow key={subject.subjectId}>
                  <TableCell>
                    {subject.displayName}
                    <br />
                    <small>{subject.subjectId}</small>
                  </TableCell>
                  <TableCell>{subject.notes || 'No notes'}</TableCell>
                  <TableCell>{new Date(subject.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button component="label" variant="outlined" size="small" disabled={uploadingSubjectId === subject.subjectId}>
                      {uploadingSubjectId === subject.subjectId ? 'Uploading...' : 'Add Face'}
                      <input hidden type="file" accept="image/*" onChange={(event) => void uploadFace(subject.subjectId, event.target.files)} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PageSection>
      </Grid>
      <Grid item xs={12} lg={5}>
        <PageSection title="Create Subject">
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField label="Display Name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            <TextField label="Notes" multiline minRows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
            <Button variant="contained" onClick={() => void create()} disabled={!displayName}>
              Create Subject
            </Button>
          </Stack>
        </PageSection>
      </Grid>
    </Grid>
  );
}
